import { useCallback } from 'react';
import { useTrussStore } from './useTrussManagement';
import { useBeamTrussStore } from './useBeamTrussManagement';

export interface TrussVisibilityControls {
  // Individual element controls
  showTrussForNode: (nodeId: string) => void;
  hideTrussForNode: (nodeId: string) => void;
  toggleTrussForNode: (nodeId: string) => void;
  
  showTrussForBeam: (beamId: string) => void;
  hideTrussForBeam: (beamId: string) => void;
  toggleTrussForBeam: (beamId: string) => void;
  
  // Global type controls
  showAllNodeTrusses: () => void;
  hideAllNodeTrusses: () => void;
  toggleAllNodeTrusses: () => void;
  
  showAllBeamTrusses: () => void;
  hideAllBeamTrusses: () => void;
  toggleAllBeamTrusses: () => void;
  
  // Utility functions
  showTruss: (type: 'beam' | 'node', id?: string) => void;
  hideTruss: (type: 'beam' | 'node', id?: string) => void;
  toggleTrussButtons: (type: 'beam' | 'node', id?: string) => void;
  
  // State getters
  areAllNodeTrussesVisible: boolean;
  areAllBeamTrussesVisible: boolean;
  getNodeTrussCount: () => number;
  getBeamTrussCount: () => number;
  isNodeTrussVisible: (nodeId: string) => boolean;
  isBeamTrussVisible: (beamId: string) => boolean;
}

/**
 * Custom hook that provides comprehensive truss visibility controls
 * for both node and beam trusses with independent state management.
 * 
 * @returns {TrussVisibilityControls} Object containing all truss visibility control functions
 */
export const useTrussVisibilityControls = (): TrussVisibilityControls => {
  const {
    toggleAllTrussVisibility,
    areAllTrussesVisible,
    showTrussForNode,
    hideTrussForNode,
    nodeTrusses
  } = useTrussStore();

  const {
    toggleAllBeamTrussVisibility,
    areAllBeamTrussesVisible,
    showTrussForBeam,
    hideTrussForBeam,
    beamTrusses
  } = useBeamTrussStore();

  // Individual node controls
  const handleShowTrussForNode = useCallback((nodeId: string) => {
    showTrussForNode(nodeId);
  }, [showTrussForNode]);

  const handleHideTrussForNode = useCallback((nodeId: string) => {
    hideTrussForNode(nodeId);
  }, [hideTrussForNode]);

  const handleToggleTrussForNode = useCallback((nodeId: string) => {
    const nodeTrussesData = nodeTrusses.get(nodeId) || [];
    const anyVisible = nodeTrussesData.some(t => t.visible);
    if (anyVisible) {
      hideTrussForNode(nodeId);
    } else {
      showTrussForNode(nodeId);
    }
  }, [nodeTrusses, hideTrussForNode, showTrussForNode]);

  // Individual beam controls
  const handleShowTrussForBeam = useCallback((beamId: string) => {
    showTrussForBeam(beamId);
  }, [showTrussForBeam]);

  const handleHideTrussForBeam = useCallback((beamId: string) => {
    hideTrussForBeam(beamId);
  }, [hideTrussForBeam]);

  const handleToggleTrussForBeam = useCallback((beamId: string) => {
    const beamTrussesData = beamTrusses.get(beamId) || [];
    const anyVisible = beamTrussesData.some(t => t.visible);
    if (anyVisible) {
      hideTrussForBeam(beamId);
    } else {
      showTrussForBeam(beamId);
    }
  }, [beamTrusses, hideTrussForBeam, showTrussForBeam]);

  // Global controls
  const handleShowAllNodeTrusses = useCallback(() => {
    toggleAllTrussVisibility(true);
  }, [toggleAllTrussVisibility]);

  const handleHideAllNodeTrusses = useCallback(() => {
    toggleAllTrussVisibility(false);
  }, [toggleAllTrussVisibility]);

  const handleToggleAllNodeTrusses = useCallback(() => {
    const allVisible = areAllTrussesVisible();
    toggleAllTrussVisibility(!allVisible);
  }, [toggleAllTrussVisibility, areAllTrussesVisible]);

  const handleShowAllBeamTrusses = useCallback(() => {
    toggleAllBeamTrussVisibility(true);
  }, [toggleAllBeamTrussVisibility]);

  const handleHideAllBeamTrusses = useCallback(() => {
    toggleAllBeamTrussVisibility(false);
  }, [toggleAllBeamTrussVisibility]);

  const handleToggleAllBeamTrusses = useCallback(() => {
    const allVisible = areAllBeamTrussesVisible();
    toggleAllBeamTrussVisibility(!allVisible);
  }, [toggleAllBeamTrussVisibility, areAllBeamTrussesVisible]);

  // Utility functions
  const showTruss = useCallback((type: 'beam' | 'node', id?: string) => {
    if (type === 'beam') {
      if (id) {
        handleShowTrussForBeam(id);
      } else {
        handleShowAllBeamTrusses();
      }
    } else {
      if (id) {
        handleShowTrussForNode(id);
      } else {
        handleShowAllNodeTrusses();
      }
    }
  }, [handleShowTrussForBeam, handleShowAllBeamTrusses, handleShowTrussForNode, handleShowAllNodeTrusses]);

  const hideTruss = useCallback((type: 'beam' | 'node', id?: string) => {
    if (type === 'beam') {
      if (id) {
        handleHideTrussForBeam(id);
      } else {
        handleHideAllBeamTrusses();
      }
    } else {
      if (id) {
        handleHideTrussForNode(id);
      } else {
        handleHideAllNodeTrusses();
      }
    }
  }, [handleHideTrussForBeam, handleHideAllBeamTrusses, handleHideTrussForNode, handleHideAllNodeTrusses]);

  const toggleTrussButtons = useCallback((type: 'beam' | 'node', id?: string) => {
    if (type === 'beam') {
      if (id) {
        handleToggleTrussForBeam(id);
      } else {
        handleToggleAllBeamTrusses();
      }
    } else {
      if (id) {
        handleToggleTrussForNode(id);
      } else {
        handleToggleAllNodeTrusses();
      }
    }
  }, [handleToggleTrussForBeam, handleToggleAllBeamTrusses, handleToggleTrussForNode, handleToggleAllNodeTrusses]);

  // State getters
  const getNodeTrussCount = useCallback(() => {
    let count = 0;
    for (const trusses of nodeTrusses.values()) {
      count += trusses.length;
    }
    return count;
  }, [nodeTrusses]);

  const getBeamTrussCount = useCallback(() => {
    let count = 0;
    for (const trusses of beamTrusses.values()) {
      count += trusses.length;
    }
    return count;
  }, [beamTrusses]);

  const isNodeTrussVisible = useCallback((nodeId: string) => {
    const nodeTrussesData = nodeTrusses.get(nodeId) || [];
    return nodeTrussesData.some(t => t.visible);
  }, [nodeTrusses]);

  const isBeamTrussVisible = useCallback((beamId: string) => {
    const beamTrussesData = beamTrusses.get(beamId) || [];
    return beamTrussesData.some(t => t.visible);
  }, [beamTrusses]);

  return {
    // Individual element controls
    showTrussForNode: handleShowTrussForNode,
    hideTrussForNode: handleHideTrussForNode,
    toggleTrussForNode: handleToggleTrussForNode,
    
    showTrussForBeam: handleShowTrussForBeam,
    hideTrussForBeam: handleHideTrussForBeam,
    toggleTrussForBeam: handleToggleTrussForBeam,
    
    // Global type controls
    showAllNodeTrusses: handleShowAllNodeTrusses,
    hideAllNodeTrusses: handleHideAllNodeTrusses,
    toggleAllNodeTrusses: handleToggleAllNodeTrusses,
    
    showAllBeamTrusses: handleShowAllBeamTrusses,
    hideAllBeamTrusses: handleHideAllBeamTrusses,
    toggleAllBeamTrusses: handleToggleAllBeamTrusses,
    
    // Utility functions
    showTruss,
    hideTruss,
    toggleTrussButtons,
    
    // State getters
    areAllNodeTrussesVisible: areAllTrussesVisible(),
    areAllBeamTrussesVisible: areAllBeamTrussesVisible(),
    getNodeTrussCount,
    getBeamTrussCount,
    isNodeTrussVisible,
    isBeamTrussVisible
  };
};
